import { Button, CheckboxWithLabel, Modal, ModalContent, OverlayService, TextInput } from '@spoiler-alert/ui-library';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import roundNumber from '../../utilities/round';

const useStyles = createUseStyles({
  modal__title: {
    fontSize: '24px',
    padding: 0,
    marginTop: 0,
    height: '25px',
    fontWeight: 500,
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1.42,
    letterSpacing: '-0.5px',
    textAlign: 'left',
    marginBottom: '8px',
  },
  modal__body: {
    fontFamily: 'Roboto',
    fontSize: '16px',
    height: '48px',
    alignSelf: 'stretch',
    lineHeight: 1.5,
    letterSpacing: '-0.18px',
    marginBottom: '8px',
  },
  form__container: {
    width: '100%',
  },
  footer__container: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: 24,
    alignItems: 'center',
  },
  modal__submit: {
    display: 'flex',
  },
  modal__button: {
    marginRight: '8px',
  },
  input: {
    marginTop: '8px',
  },
});

const ReconcileQuantityModal = ({ open, onHide, rowData, onSubmit, loading, field }) => {
  const classes = useStyles();
  const [errors, setErrors] = useState({});
  const [fieldValue, setFieldValue] = useState();
  const [adjustLeftoverQuantity, setAdjustLeftoverQuantity] = useState(false);

  const handleLeftoverQuantityChange = () => {
    setAdjustLeftoverQuantity(!adjustLeftoverQuantity);
  };

  useEffect(() => {
    if (open) OverlayService.show();
    return () => OverlayService.hide();
  }, []);

  useEffect(() => {
    if (!open) {
      setFieldValue();
      setErrors({});
      setAdjustLeftoverQuantity(false);
    } else {
      setFieldValue(
        field === 'quantity' ? rowData?.transaction[field] : roundNumber(rowData?.transaction.totalSalePrice / rowData?.transaction.quantity, 2),
      );
    }
  }, [open]);

  const checkValue = (valueUpdate, field, setValue) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: valueUpdate.trim() === '' || isNaN(valueUpdate) || Number(valueUpdate) < 0,
    }));
    setValue(valueUpdate);
  };

  return (
    <Modal onHide={onHide} open={open} closeOnEsc closeOnOutsideClick>
      <div className={classes.form__container}>
        <ModalContent>
          <div data-testid="modal-title" className={classes.modal__title}>
            Reconcile Transaction
          </div>
          <div data-testid="modal-body" className={classes.modal__body} style={{ marginBottom: 0 }}>
            {`Input the ${field} you would like to reconcile for transaction ${rowData?.transaction?.id}`}
          </div>
          <div className={classes.input}>
            <TextInput
              onChange={(value) => checkValue(value, field, setFieldValue)}
              error={errors[field]}
              type="number"
              labelText={`Input final adjusted ${field}`}
              value={fieldValue}
              min={0}
              required
            />
            {field === 'quantity' && (
              <CheckboxWithLabel
                label="Reduce leftover quantity accordingly"
                disabled={loading || rowData?.transaction?.quantity <= fieldValue}
                checked={adjustLeftoverQuantity}
                id="reduceLeftoverQuantity"
                value={adjustLeftoverQuantity}
                onChecked={handleLeftoverQuantityChange}
              />
            )}
          </div>
        </ModalContent>
        <div className={classes.footer__container}>
          <div className={classes.modal__submit}>
            <Button
              data-testid="submit-button"
              className={classes.modal__button}
              type="button"
              onClick={() => onSubmit(fieldValue, adjustLeftoverQuantity)}
              disabled={errors[field] || !fieldValue}
              primary
              loading={loading}
              loadingText={'Submitting...'}
            >
              Submit
            </Button>
            <Button data-testid="cancel-button" className={classes.modal__cancel} type="button" onClick={onHide} warning>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

ReconcileQuantityModal.propTypes = {
  onHide: PropTypes.func,
  open: PropTypes.bool,
  rowData: PropTypes.object,
  onSubmit: PropTypes.func,
  loading: PropTypes.bool,
  field: PropTypes.string,
};

export default ReconcileQuantityModal;
